import { COLORS, RADIUS, SHADOWS, fontFamilyFor } from "../../theme";

const heading = (isArabic) => (isArabic ? fontFamilyFor("ar") : "'Playfair Display', serif");
const body = (isArabic) => fontFamilyFor(isArabic ? "ar" : "en");

export const Style = {
  page: {
    background: COLORS.dark,
    minHeight: "100vh",
  },
  hero: {
    textAlign: "center",
    padding: { xs: "48px 20px 16px", md: "64px 48px 24px" },
  },
  heroTitle: (isArabic) => ({
    fontFamily: heading(isArabic),
    fontSize: { xs: "2.6rem", md: "5rem" },
    fontWeight: 700,
    mb: "16px",
    background: `linear-gradient(135deg, #fff 0%, ${COLORS.peach} 100%)`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  }),
  heroSubtitle: (isArabic) => ({
    fontFamily: body(isArabic),
    fontSize: { xs: "1.05rem", md: "1.3rem" },
    color: "rgba(255,255,255,0.7)",
  }),
  grid: {
    maxWidth: "1200px",
    margin: { xs: "32px auto", md: "64px auto" },
    padding: { xs: "0 16px", md: "0 48px" },
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
    gap: "32px",
  },
  card: (featured) => ({
    background: featured
      ? `linear-gradient(135deg, ${COLORS.gray} 0%, #2a2520 100%)`
      : COLORS.gray,
    padding: { xs: "32px 24px", md: "48px 32px" },
    borderRadius: RADIUS.card,
    border: `1px solid ${featured ? COLORS.peach : "rgba(255,255,255,0.05)"}`,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.3s ease, border-color 0.3s ease",
    transform: featured ? { md: "translateY(-12px)" } : "none",
    "&:hover": { transform: "translateY(-8px)", borderColor: COLORS.peach },
  }),
  popularBadge: (isArabic) => ({
    position: "absolute",
    top: "-14px",
    left: "50%",
    transform: "translateX(-50%)",
    background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.peach})`,
    color: COLORS.dark,
    fontFamily: body(isArabic),
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "6px 20px",
    borderRadius: RADIUS.pill,
    whiteSpace: "nowrap",
  }),
  planName: (isArabic) => ({
    fontFamily: body(isArabic),
    fontSize: "1.5rem",
    fontWeight: 700,
    color: COLORS.peach,
    mb: "12px",
  }),
  planPrice: (isArabic) => ({
    fontFamily: body(isArabic),
    fontSize: "3rem",
    fontWeight: 800,
    color: "#fff",
    mb: "6px",
  }),
  perMonth: { fontSize: "1.2rem", color: "rgba(255,255,255,0.6)" },
  planDesc: (isArabic) => ({
    fontFamily: body(isArabic),
    color: "rgba(255,255,255,0.7)",
    fontSize: "15px",
    mb: "24px",
  }),
  featureList: { listStyle: "none", m: 0, p: 0, mb: "28px", flex: 1 },
  featureItem: (isArabic) => ({
    display: "flex",
    flexDirection: isArabic ? "row-reverse" : "row",
    alignItems: "flex-start",
    gap: "8px",
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    color: "rgba(255,255,255,0.85)",
    fontFamily: body(isArabic),
    fontSize: "14.5px",
  }),
  check: { color: COLORS.peach, fontWeight: 700 },
  planButton: (isArabic, featured) => ({
    width: "100%",
    py: "14px",
    borderRadius: RADIUS.pill,
    background: featured ? COLORS.peach : "transparent",
    border: featured ? "none" : `1px solid rgba(255,255,255,0.25)`,
    color: featured ? COLORS.dark : "#fff",
    fontFamily: body(isArabic),
    fontWeight: 700,
    fontSize: "15px",
    "&:hover": {
      background: COLORS.gold,
      color: COLORS.dark,
      borderColor: COLORS.gold,
    },
  }),
  enterpriseSection: {
    maxWidth: "1200px",
    margin: { xs: "48px auto", md: "80px auto" },
    padding: { xs: "0 16px", md: "0 48px" },
  },
  sectionHeading: (isArabic) => ({
    fontFamily: heading(isArabic),
    fontSize: { xs: "2rem", md: "2.8rem" },
    textAlign: "center",
    color: COLORS.peach,
    mb: "40px",
  }),
  enterpriseGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: "24px",
  },
  enterpriseCard: {
    background: COLORS.gray,
    borderRadius: RADIUS.card,
    border: "1px solid rgba(255,255,255,0.05)",
    padding: "28px",
  },
  enterpriseCardTitle: (isArabic) => ({
    fontFamily: body(isArabic),
    fontWeight: 700,
    fontSize: "17px",
    color: "#fff",
    mb: "10px",
  }),
  enterpriseCardBody: (isArabic) => ({
    fontFamily: body(isArabic),
    color: "rgba(255,255,255,0.7)",
    fontSize: "14.5px",
    lineHeight: 1.6,
  }),
  faq: {
    maxWidth: "800px",
    margin: { xs: "48px auto", md: "80px auto" },
    padding: { xs: "0 16px", md: "0 48px" },
  },
  faqTitle: (isArabic) => ({
    fontFamily: heading(isArabic),
    fontSize: { xs: "2rem", md: "3rem" },
    textAlign: "center",
    color: COLORS.peach,
    mb: "40px",
  }),
  accordion: {
    background: COLORS.gray,
    color: "#fff",
    borderRadius: `${RADIUS.card} !important`,
    border: "1px solid rgba(255,255,255,0.05)",
    mb: "12px",
    boxShadow: "none",
    "&:before": { display: "none" },
  },
  accordionQuestion: (isArabic) => ({
    fontFamily: body(isArabic),
    fontWeight: 700,
    fontSize: "16px",
    color: COLORS.peach,
  }),
  accordionAnswer: (isArabic) => ({
    fontFamily: body(isArabic),
    color: "rgba(255,255,255,0.8)",
    lineHeight: 1.6,
    fontSize: "14.5px",
  }),
};
